import { useCallback, useContext } from 'react';

import { useForm } from 'react-final-form';

import { Button, Icons } from '@cast/design-system';

import { PreparedWorkload } from 'features/workload-optimization';

import { useOverrideGroup } from './OverrideGroupProvider';
import { WorkloadDetailsContext } from '../../../_context';
import { useCanEditWorkloadField } from '../../../_hooks/useCanEditWorkloadField';

import { useControlsGroupState, useRffFormState } from '@cast/shared-rff-ui';

type Props<T> = {
  onRevert: (workload: PreparedWorkload) => Omit<T, 'isOverridden'>;
};

export function OverrideGroupOverrideButton<T>({ onRevert }: Props<T>) {
  const { workload } = useContext(WorkloadDetailsContext);

  const form = useForm();
  const { mode } = useRffFormState();

  const { isOverriddenInput, groupName, canOverride, name } =
    useOverrideGroup();

  const { disabled } = useControlsGroupState({
    disabled: mode === 'view',
  });

  const { canEditWorkloadField } = useCanEditWorkloadField(workload!);
  const { isNamespaceWritable } = useContext(WorkloadDetailsContext);
  const isNamespaceWritableForWorkload = isNamespaceWritable(
    workload?.namespace
  );

  const onOverride = useCallback(() => {
    form.change(isOverriddenInput.name, true);
  }, [form, isOverriddenInput.name]);

  const handleRevert = useCallback(() => {
    if (!groupName || !workload || !onRevert) {
      return;
    }

    const reverted = onRevert(workload);

    form.change(groupName, {
      ...reverted,
      isOverridden: false,
    });
  }, [form, groupName, onRevert, workload]);

  if (
    !canOverride ||
    workload?.isManagedByAnnotations ||
    !isNamespaceWritableForWorkload
  ) {
    return null;
  }

  return isOverriddenInput.value ? (
    <Button
      testId={`revert-policy-${name}`}
      variant="text"
      endIcon={<Icons.ArrowCounterClockwise />}
      onClick={handleRevert}
      disabled={!canEditWorkloadField || !!disabled}
      size="md"
    >
      Revert to policy values
    </Button>
  ) : (
    <Button
      testId={`override-policy-${name}`}
      variant="text"
      endIcon={<Icons.CastHorizontalLinkDash />}
      onClick={onOverride}
      disabled={!canEditWorkloadField || !!disabled}
      size="md"
    >
      Override
    </Button>
  );
}
